import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { AxiosError } from 'axios';

export type ChartPreference = {
  id: string | null;
  temperature_grid_size: number | null;
  min_temperature: number | null;
  max_temperature: number | null;
  time_grid_side: number | null;
  start_timestamp: number | null;
  end_timestamp: number | null;
  colors: string[]
  isDefaultTemplate: boolean;
};

export type UpdateChartPreferenceDto = Required<Omit<ChartPreference, 'colors' | 'isDefaultTemplate'>>;

const DEFAULT_STATE: { chartPreference: ChartPreference | null } = {
  chartPreference: null,
};

export const chart_preference = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    setOneData(state, payload) {
      return {
        ...state,
        chartPreference: {
          ...state.chartPreference,
          ...payload,
        },
      };
    },
    setOne(state, payload) {
      return {
        ...state,
        chartPreference: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        chartPreference: DEFAULT_STATE.chartPreference,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getOneByCycleId(cycleId: string): Promise<ChartPreference> {
        try {
          const path = generatePath(URL.CHART_PREFERENCE_CYCLE, { cycleId });
          const response = await http.get(path);

          dispatch.chart_preference.setOne(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(
              error.response?.data?.message || 'An unexpected error occurred'
            );
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async update(data: {
        cycleId: string;
        payload: UpdateChartPreferenceDto;
      }): Promise<ChartPreference> {
        try {
          const path = generatePath(URL.CHART_PREFERENCE_CYCLE, { cycleId: data.cycleId });
          const response = await http.put(path, data.payload);

          dispatch.chart_preference.setOne(response.data);
          dispatch.cycles.setChartPreference(response.data);
          toast.success('Chart preference updated successfully');

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(
              error.response?.data?.message || 'An unexpected error occurred'
            );
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
    };
  },
});
